import { Button, Icon, useObsinaI18n } from "@obsina/design-system";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Clock,
  Mail,
  ScanText,
  X,
} from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import {
  claimStateWords,
  ENTITY_ICON_COMPONENT,
  EVIDENCE_CHANNEL_ICON_COMPONENT,
  EVIDENCE_ICON_COMPONENT,
  badgeText,
  foldForSearch,
  formatDate,
  formatInstant,
  relationLabel,
  severestClaimState,
  toneVars,
} from "./GraphView";
import type {
  ClaimState,
  EmailHeaderField,
  EntityIdentifier,
  GraphNode,
  InspectorEvidenceRow,
  InspectorModel,
  InspectorOpenItem,
  MemoryGraphMessages,
} from "./types";
import "./memory-graph.css";

/**
 * One entity this panel's subject is linked to on the drawn map, named rather
 * than counted. Derived by the view from the same resolved graph the canvas
 * draws, so the list is exactly the node's degree — it can neither name an
 * entity the map does not hold nor widen what the reader is authorized to see.
 */
export interface InspectorNeighbour {
  readonly id: string;
  readonly label: string;
  /**
   * The address this record was recognised by, when the surface carries one.
   *
   * It is here because a name is not an identity. Measured on the live tenant on
   * 2026-07-29, 13 of 103 people share a name with another record — one group of
   * seven — and a card listing two of them printed the same name twice, which
   * reads as a system that duplicated a row. It did not: those are two records,
   * most likely one person's two addresses, and Obsina deliberately does not
   * merge them. The address is what turns an apparent repeat into the two
   * different things it always was.
   */
  readonly identifier?: EntityIdentifier;
  /** How many identifiers the record holds; lets one of several say so. */
  readonly identifierCount?: number;
  /** Relation vocabulary term, or null for an unlabeled structural link. */
  readonly relationType: string | null;
  /** Backing evidence on the merged edge; orders the strongest link first. */
  readonly weight: number;
  /** Assertion states on the edge; absent until the parser carries them. */
  readonly states?: readonly ClaimState[];
}

export interface InspectorPanelProps {
  /** Whether the panel is shown. Rendered as nothing while closed. */
  readonly open: boolean;
  /**
   * The focused graph node: the stable source of the title, type badge and link
   * count while the model loads, and null only when the panel is closed.
   */
  readonly node: GraphNode | null;
  readonly model: InspectorModel | null;
  readonly loading: boolean;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly messages: MemoryGraphMessages;
  readonly onClose: () => void;
  /** Opens the source reference for one evidence row; omitted → no control. */
  readonly onOpenEvidence?: (evidenceId: string) => void;
  /** The focused node's neighbours on the drawn map, named. */
  readonly neighbours?: readonly InspectorNeighbour[];
  /** True when the map itself is bounded, so the named list may not be whole. */
  readonly mapBounded?: boolean;
  /** Moves the map's focus to a neighbour; omitted → the names are not links. */
  readonly onFocusNode?: (nodeId: string) => void;
}

/**
 * Focused-entity detail, presented as a **non-modal** floating card over the
 * right of the graph (P4 reference). Non-modal is deliberate: the graph behind
 * stays fully interactive, so clicking another node just switches this panel's
 * content. That rules out a native modal `<dialog>` (its inert backdrop would
 * block the graph), so this is a hand-built accessible dialog: `role="dialog"`
 * without `aria-modal`, focus moved to the panel on open, `Escape` closes, focus
 * returns to the node that opened it on close, and no focus trap (Tab flows on to
 * the rest of the page). On the narrow layout it becomes a full-screen sheet
 * (see memory-graph.css). There is deliberately no "Ask about this entity"
 * affordance: the MVP web has no Ask surface and a dead control is forbidden.
 *
 * The reading order answers the four questions a reader actually has, in order:
 * *who or what is this* (name and type), *who is it linked to* (the neighbours,
 * by name), *what was promised, by whom* (the open promises), and *where does
 * any of this come from* (the verbatim excerpts, then the coverage watermark in
 * the footer). Nothing that fails to change a decision is drawn: no opaque
 * commitment id, no owner line repeating a heading that already says it, and no
 * due date repeated by the badge next to it.
 *
 * Every label here is written for a reader who has never seen this product's
 * vocabulary. The counts this panel used to lead with — "3 connections · 39
 * evidence excerpts" — are gone, not because they were untrue but because a
 * number is not an answer: the connections are now named and the evidence total
 * is stated where the excerpts are, saying which page of them this is.
 */
export function InspectorPanel({
  open,
  node,
  model,
  loading,
  failed,
  onRetry,
  messages,
  onClose,
  onOpenEvidence,
  neighbours,
  mapBounded = false,
  onFocusNode,
}: InspectorPanelProps) {
  const { locale } = useObsinaI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const titleId = useId();

  // Move focus into the panel when it opens and return it to the node that
  // opened it when it closes — without trapping focus while open.
  //
  // Except when the opener is a text field. The search box focuses the graph on
  // every keystroke, so pulling focus here on open meant the first character
  // that matched anything tore the caret out of the box and the rest of the
  // name was typed into the panel — the search read as simply broken. A reader
  // who is still typing keeps their caret; the panel is beside them either way,
  // and Tab reaches it because nothing here traps focus.
  useEffect(() => {
    if (open && !wasOpen.current) {
      wasOpen.current = true;
      const active = document.activeElement;
      openerRef.current = active instanceof HTMLElement ? active : null;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement;
      if (!typing) panelRef.current?.focus();
    } else if (!open && wasOpen.current) {
      wasOpen.current = false;
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener && document.contains(opener)) opener.focus();
    }
  }, [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
    }
  };

  if (!open || !node) return null;

  const typeLabel = messages.entityType[node.entityType];
  // A missing watermark is stated, never rendered as an empty read-up-to line.
  const asOfText = model
    ? model.asOf
      ? messages.inspector.asOf(formatInstant(model.asOf, locale) ?? model.asOf)
      : messages.inspector.asOfUnknown
    : null;

  return (
    <div
      aria-labelledby={titleId}
      className="mg-inspector"
      onKeyDown={onKeyDown}
      ref={panelRef}
      role="dialog"
      tabIndex={-1}
    >
      {/* Name and kind read as one identity, so the kind rides the title row
          instead of taking a line of its own beneath it. */}
      <div className="mg-inspector__head">
        <div className="mg-inspector__ident">
          <h2 className="mg-inspector__name" id={titleId}>
            {node.label}
          </h2>
          <span className="mg-inspector__type">
            <Icon icon={ENTITY_ICON_COMPONENT[node.entityType]} size={13} />
            {typeLabel}
          </span>
          {/* A record that was merged away or retired says so where its kind is
              stated — in words, next to the ring that marks it on the map. */}
          {node.status && messages.entityStatus[node.status] && (
            <span className="mg-inspector__retired">
              {messages.entityStatus[node.status]}
            </span>
          )}
        </div>
        <button
          aria-label={messages.inspector.close}
          className="mg-inspector__close"
          onClick={onClose}
          type="button"
        >
          <Icon icon={X} size={18} />
        </button>
      </div>

      <div className="mg-inspector__body">
        <ConnectionsSection
          entityName={node.label}
          mapBounded={mapBounded}
          messages={messages}
          neighbours={neighbours ?? []}
          onFocusNode={onFocusNode}
        />
        {model ? (
          <InspectorContent
            entityName={model.name}
            locale={locale}
            messages={messages}
            model={model}
            onOpenEvidence={onOpenEvidence}
          />
        ) : failed ? (
          <div className="mg-idlg__status" role="alert">
            <p className="mg-idlg__status-body">{messages.error.body}</p>
            <Button onClick={onRetry} size="sm" variant="secondary">
              {messages.error.retry}
            </Button>
          </div>
        ) : (
          <p aria-busy={loading} className="mg-idlg__status-body" role="status">
            {messages.loading}
          </p>
        )}
      </div>

      {asOfText && (
        <p className="mg-inspector__foot">
          <Icon icon={Clock} size={12} />
          {asOfText}
        </p>
      )}
    </div>
  );
}

/**
 * The neighbours, named and grouped by what the link actually is — the same
 * wording the map prints on the edge itself, so the panel and the canvas agree
 * instead of one saying "Emailed" and the other "3 connections".
 *
 * The strongest link leads: neighbours sort by backing evidence (descending),
 * then by label, and the groups keep that order. Ordering is total and
 * locale-independent so the same graph always renders the same list.
 */
/**
 * What a neighbour chip says: the name it leads with, the quieter line beside
 * it, and the single sentence a screen reader hears for both.
 *
 * Two rules, and the second is the one that matters. The address is *secondary*
 * — a name still leads, the chip does not grow into a two-line card, and the
 * list stays scannable. And a record whose name is only its own address does
 * not get to print that address as a name: it says so, and the address moves to
 * the same quiet position it occupies on every other row. Measured on the live
 * tenant on 2026-07-29, 14 of 103 people are in exactly that state, which is
 * why the list read as inconsistent — five names and a raw mailbox.
 *
 * No grouping and no merging happens here or anywhere else. Two chips with the
 * same name stay two chips; all that changes is that a reader can now see why.
 */
function neighbourNaming(
  neighbour: InspectorNeighbour,
  messages: MemoryGraphMessages,
): {
  readonly name: string;
  readonly detail: string | null;
  readonly accessible: string;
} {
  const address = neighbour.identifier?.value ?? null;
  const nameIsTheAddress =
    address !== null &&
    neighbour.label.trim().toLowerCase() === address.trim().toLowerCase();
  const name = nameIsTheAddress
    ? messages.inspector.connectionUnnamed
    : neighbour.label;
  if (address === null) {
    return { name, detail: null, accessible: name };
  }
  // "one of N" only when there really are others. On the live tenant every
  // person holds exactly one, so this reads as a plain address today — and the
  // moment one holds two, the chip stops implying this is the only one.
  const detail =
    (neighbour.identifierCount ?? 1) > 1
      ? messages.inspector.connectionIdentifierOfMany(
          address,
          neighbour.identifierCount ?? 1,
        )
      : address;
  return {
    name,
    detail,
    accessible: messages.inspector.connectionNamed(name, detail),
  };
}

/** One relation kind — and how far it can be trusted — with the names it holds. */
interface NeighbourGroup {
  label: string;
  items: InspectorNeighbour[];
  state: ClaimState | null;
}

/**
 * How many names stand open before the list folds, and how many of them the
 * summary line leads with.
 *
 * Eleven address chips in a flat run is what a reader met on the live tenant,
 * and it answered nothing: every name carried the same visual weight, so the
 * one this entity actually deals with was indistinguishable from the tenth. Up
 * to {@link CONNECTIONS_INLINE_LIMIT} the whole list *is* the answer and folding
 * it would only add a click. Past it, the two best-evidenced names lead and the
 * rest are one activation away.
 */
const CONNECTIONS_INLINE_LIMIT = 5;
const CONNECTIONS_LEAD_COUNT = 2;

/** One neighbour chip: a jump target when the map can move, else plain text. */
function NeighbourChip({
  neighbour,
  messages,
  onFocusNode,
}: {
  neighbour: InspectorNeighbour;
  messages: MemoryGraphMessages;
  onFocusNode?: (nodeId: string) => void;
}) {
  const named = neighbourNaming(neighbour, messages);
  // Name and address are two spans in one chip, never two controls: a reader
  // hears "Erkan Öztürk, erkan@… — show on the map" as a single thing to act
  // on. The button's own `aria-label` already replaces its children, so the
  // visible address needs no aria-hidden; the static chip has no label at all
  // and its two spans read as one run of text.
  const body = (
    <>
      <span className="mg-chip__name">{named.name}</span>
      {named.detail !== null && (
        <span className="mg-chip__detail">{named.detail}</span>
      )}
    </>
  );
  return onFocusNode ? (
    <button
      aria-label={messages.inspector.connectionFocus(named.accessible)}
      className="mg-chip"
      onClick={() => onFocusNode(neighbour.id)}
      type="button"
    >
      {body}
    </button>
  ) : (
    <span className="mg-chip">{body}</span>
  );
}

function ConnectionsSection({
  neighbours,
  entityName,
  messages,
  mapBounded,
  onFocusNode,
}: {
  neighbours: readonly InspectorNeighbour[];
  entityName: string;
  messages: MemoryGraphMessages;
  mapBounded: boolean;
  onFocusNode?: (nodeId: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const groups: NeighbourGroup[] = [];
  const byLabel = new Map<string, NeighbourGroup>();
  const ordered = [...neighbours].sort(
    (left, right) =>
      right.weight - left.weight || (left.label < right.label ? -1 : 1),
  );
  for (const neighbour of ordered) {
    // The trust travels with the relation, so it groups with it: "Emailed" and
    // "Emailed · not confirmed" are two different statements about a link and
    // may not be pooled under one heading as if they were the same.
    const words = [
      neighbour.relationType
        ? relationLabel(neighbour.relationType, messages)
        : messages.inspector.connectionUnlabeled,
      ...claimStateWords(neighbour.states, messages),
    ];
    const label = words.join(" · ");
    let group = byLabel.get(label);
    if (!group) {
      group = {
        label,
        items: [],
        state: severestClaimState(neighbour.states),
      };
      byLabel.set(label, group);
      groups.push(group);
    }
    group.items.push(neighbour);
  }

  const folds = ordered.length > CONNECTIONS_INLINE_LIMIT;
  const leaders = ordered.slice(0, CONNECTIONS_LEAD_COUNT);
  const rest = ordered.length - leaders.length;
  // Kept mounted while folded rather than unmounted, so the control that opens
  // it points at something that exists: `aria-controls` naming a missing id is
  // a promise the panel does not keep.
  const fullList = (
    <ul
      className="mg-links"
      hidden={folds && !expanded}
      id={folds ? listId : undefined}
    >
      {groups.map((group) => (
        <li
          className="mg-links__group"
          data-claim-state={group.state ?? undefined}
          key={group.label}
        >
          <span className="mg-links__kind">{group.label}</span>
          <span className="mg-links__names">
            {group.items.map((neighbour) => (
              <NeighbourChip
                key={neighbour.id}
                messages={messages}
                neighbour={neighbour}
                onFocusNode={onFocusNode}
              />
            ))}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <section className="mg-section">
      <h3 className="mg-section__head">
        {messages.inspector.connectionsSection}
      </h3>
      {groups.length === 0 ? (
        <p className="mg-section__empty">
          {messages.inspector.connectionsEmpty(entityName)}
        </p>
      ) : (
        <>
          {folds ? (
            <>
              {/* Folded, the answer is the two names this entity is most
                  evidenced with, and the control that opens the rest rides the
                  same row — the half of the line the names leave over belongs
                  to what the names are missing, not to empty space. */}
              {/* The control keeps its place in the tree across both states,
                  so React keeps the same DOM node and a reader who opened the
                  list from the keyboard still has focus on the control that
                  closes it. Rebuilding it dropped focus to the document. */}
              <div className="mg-links__lead">
                {!expanded && (
                  <>
                    <span className="mg-links__kind">
                      {messages.inspector.connectionsLead}
                    </span>
                    <span className="mg-links__names">
                      {leaders.map((neighbour) => (
                        <NeighbourChip
                          key={neighbour.id}
                          messages={messages}
                          neighbour={neighbour}
                          onFocusNode={onFocusNode}
                        />
                      ))}
                    </span>
                  </>
                )}
                <button
                  aria-controls={listId}
                  aria-expanded={expanded}
                  className="mg-links__toggle"
                  onClick={() => setExpanded((value) => !value)}
                  type="button"
                >
                  <Icon
                    icon={expanded ? ChevronDown : ChevronRight}
                    size={14}
                  />
                  {expanded
                    ? messages.inspector.connectionsCollapse
                    : messages.inspector.connectionsRest(rest)}
                </button>
              </div>
              {fullList}
            </>
          ) : (
            fullList
          )}
          {/* A named list looks complete; a bounded map's list is not. */}
          {mapBounded && (
            <p className="mg-section__note mg-links__bound">
              {messages.inspector.connectionsBounded}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Splits the panel's excerpts into what a reader came for and what the extractor
 * happened to capture.
 *
 * An entity's evidence is frequently a run of verbatim recipient lines — five
 * copies of `- Cc: …` naming the same six addresses. Each one really does bind
 * the entity to an authorized source, and none of them tells a reader anything
 * the count does not. So the substantive excerpts lead, in their original order,
 * and the header lines fold into one group behind them. Identical quotes on the
 * same day are collapsed to one: a duplicate is not a second piece of evidence.
 * Nothing is dropped — the group states its size and opens to the verbatim
 * lines.
 */
function partitionEvidence(rows: readonly InspectorEvidenceRow[]): {
  readonly quotes: readonly InspectorEvidenceRow[];
  readonly headers: readonly InspectorEvidenceRow[];
} {
  const quotes: InspectorEvidenceRow[] = [];
  const headers: InspectorEvidenceRow[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const fingerprint = `${row.quote}${row.occurredAt ?? ""}`;
    if (seen.has(fingerprint)) continue;
    seen.add(fingerprint);
    (row.isHeaderLine ? headers : quotes).push(row);
  }
  return { quotes, headers };
}

/* ------------------------------------------------------------------------- *
 * Reading a captured header line back to a person
 *
 * The extractor stores what it found, verbatim, and a recipient line is a real
 * piece of evidence: it binds this entity to an authorized source. But under the
 * heading "where does this come from", `- Cc: Zahide Öztürk <zahide@…>, Funda
 * Özlü <funda@…>, office@festavolo.eu <office@festavolo.eu>` answers nothing.
 * It is the mail system talking to itself. The single fact inside it — this
 * address was on a message, next to two named people — is there to be read off
 * the line rather than summarised: the field is the field, the names are the
 * names, the date is the record's own. Nothing here rewrites, infers or drops
 * anything, and the verbatim line is still one activation away.
 * ------------------------------------------------------------------------- */

/** Header prefixes this build recognises, mapped onto the typed field. */
const HEADER_FIELDS: readonly (readonly [RegExp, EmailHeaderField])[] = [
  [/^reply-to$/i, "replyTo"],
  [/^to$/i, "to"],
  [/^from$/i, "from"],
  [/^cc$/i, "cc"],
  [/^bcc$/i, "bcc"],
  [/^subject$/i, "subject"],
  [/^date$/i, "date"],
  [/^sent$/i, "sent"],
];

/** Leading quote/list punctuation, then `Field:`, then the rest. */
const HEADER_SPLIT = /^\s*[-–—>*\s]*([A-Za-z-]+)\s*:\s*(.*)$/s;

/** Other parties spelled out before the sentence starts counting. */
const HEADER_OTHERS_SHOWN = 3;

export interface HeaderMention {
  readonly field: EmailHeaderField;
  /** Parties named on the line other than the panel's own subject. */
  readonly others: readonly string[];
  /** How many further parties the line named beyond {@link others}. */
  readonly overflow: number;
  /** The subject text, for a `subject:` line; null for every other field. */
  readonly subject: string | null;
}

/**
 * Splits an address list on the separators between entries, ignoring the ones
 * inside a `<...>` address or a "Quoted, Name".
 */
function splitAddressList(value: string): string[] {
  const parts: string[] = [];
  let current = "";
  let inAngle = false;
  let inQuote = false;
  for (const char of value) {
    if (char === '"') inQuote = !inQuote;
    else if (char === "<") inAngle = true;
    else if (char === ">") inAngle = false;
    if ((char === "," || char === ";") && !inAngle && !inQuote) {
      parts.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  parts.push(current);
  return parts.map((part) => part.trim()).filter((part) => part !== "");
}

/** The readable half of one entry: its display name, else the bare address. */
function addressLabel(entry: string): string {
  const named = /^(.*?)\s*<([^>]*)>\s*$/s.exec(entry);
  const display = named ? named[1].replace(/^"|"$/g, "").trim() : "";
  if (display !== "") return display;
  return (named ? named[2] : entry).trim();
}

/**
 * Whether an entry names the panel's own subject, which the sentence must not
 * list as somebody it was mentioned "alongside".
 *
 * Compared on folded letters with separators removed, because the two forms of
 * the same person rarely match literally: the entity is "Tahir Korkut" and the
 * line says `tahir.korkut@example.test`. Case, diacritics and the dotted/dotless
 * `i` are handled by the same fold the graph search uses.
 */
function namesSubject(entry: string, subjectName: string): boolean {
  const strip = (value: string): string =>
    foldForSearch(value).replace(/[^a-z0-9]/g, "");
  const subject = strip(subjectName);
  if (subject === "") return false;
  const named = /^(.*?)\s*<([^>]*)>\s*$/s.exec(entry);
  const candidates = named ? [named[1], named[2]] : [entry];
  return candidates.some((candidate) => {
    const folded = strip(candidate);
    if (folded === "") return false;
    // The local part alone, so `tahir.korkut@example.test` matches the person
    // without the domain dragging an unrelated tenant name into the comparison.
    const local = strip(candidate.split("@")[0] ?? "");
    return (
      folded === subject ||
      local === subject ||
      folded.startsWith(subject) ||
      subject.startsWith(local !== "" ? local : folded)
    );
  });
}

/**
 * Reads a captured header line, or null when the quote is not one this build
 * recognises — in which case the caller keeps saying what it always said rather
 * than inventing a shape for it.
 */
export function parseHeaderMention(
  quote: string,
  subjectName: string,
): HeaderMention | null {
  const split = HEADER_SPLIT.exec(quote);
  if (!split) return null;
  const field = HEADER_FIELDS.find(([pattern]) => pattern.test(split[1]))?.[1];
  if (!field) return null;
  const rest = split[2].trim();
  if (field === "subject") {
    return rest === ""
      ? null
      : { field, others: [], overflow: 0, subject: rest };
  }
  const named = splitAddressList(rest)
    .filter((entry) => !namesSubject(entry, subjectName))
    .map(addressLabel)
    .filter((label) => label !== "");
  // Same party twice on one line is one party.
  const unique = [...new Set(named)];
  return {
    field,
    others: unique.slice(0, HEADER_OTHERS_SHOWN),
    overflow: Math.max(0, unique.length - HEADER_OTHERS_SHOWN),
    subject: null,
  };
}

/** The sentence for one header mention, or null when it cannot be read. */
function headerMentionText(
  row: InspectorEvidenceRow,
  subjectName: string,
  messages: MemoryGraphMessages,
  locale: string,
): string | null {
  const mention = parseHeaderMention(row.quote, subjectName);
  if (!mention) return null;
  const date =
    formatDate(row.occurredAt, locale) ??
    messages.inspector.evidenceDateUnknown;
  if (mention.subject !== null) {
    return messages.inspector.evidenceHeaderMentionSubject(
      date,
      mention.subject,
    );
  }
  const others =
    mention.others.length > 0
      ? messages.inspector.evidenceHeaderOthers(
          mention.others,
          mention.overflow,
        )
      : null;
  return messages.inspector.evidenceHeaderMention(
    date,
    messages.inspector.evidenceHeaderFieldName[mention.field],
    others,
  );
}

/** Earliest and latest instant across a group, or null when none is known. */
function instantRange(
  rows: readonly InspectorEvidenceRow[],
): { readonly from: string; readonly to: string } | null {
  const instants = rows
    .map((row) => row.occurredAt)
    .filter((value): value is string => value !== null)
    .sort();
  const from = instants[0];
  const to = instants.at(-1);
  return from && to ? { from, to } : null;
}

function InspectorContent({
  model,
  entityName,
  messages,
  locale,
  onOpenEvidence,
}: {
  model: InspectorModel;
  /** The panel's own subject, so an open item never repeats it as an owner. */
  entityName: string;
  messages: MemoryGraphMessages;
  locale: string;
  onOpenEvidence?: (evidenceId: string) => void;
}) {
  // A truncated count is a lower bound, so there is more even when the shown
  // rows happen to reach it — the note has to appear either way. While the page
  // is still being read there is no "shown" to state, so nothing is derived
  // from a row count that only means "not arrived".
  const hiddenEvidence = model.evidencePending
    ? 0
    : model.evidenceTruncated
      ? Math.max(1, model.viewAllCount - model.evidenceRows.length)
      : model.viewAllCount - model.evidenceRows.length;
  const { quotes, headers } = partitionEvidence(model.evidenceRows);
  // Who promised, said once. When every promise here belongs to the same person
  // the heading carries it, so the rows do not repeat an owner line each; when
  // they belong to different people the heading cannot claim one, and each row
  // names its own. Either way the reader is never left guessing whose promise
  // they are reading — which is what a bare "Open items" left open.
  const owners = new Set(
    model.openItems
      .map((item) => item.ownerLabel)
      .filter((label): label is string => label !== null),
  );
  const everyItemOwned = model.openItems.every(
    (item) => item.ownerLabel !== null,
  );
  const soleOwner =
    owners.size === 1 && everyItemOwned ? ([...owners][0] ?? null) : null;
  // And the same for whom they were made to. Three promises to one company
  // printed "Promised to Arcadia Grid Solutions" three times — the same sentence
  // on three lines, which is the repetition the density rule forbids, and it
  // cost enough height to push the evidence below the fold. When every promise
  // here shares one answer — a name, or no recipient at all — it is stated once
  // beside the heading. It is still stated: an absent recipient stays an answer
  // rather than a blank, exactly as the per-row line said it. A mixed set makes
  // no shared claim, so those rows keep naming their own.
  const counterparties = new Set(
    model.openItems.map((item) => item.counterpartyLabel),
  );
  const shared =
    model.openItems.length > 0 && counterparties.size === 1
      ? { recipient: [...counterparties][0] ?? null }
      : null;
  // One promiser and one recipient become one sentence — "What X promised Y" —
  // rather than a heading with a second clause hung off its right edge, which
  // wrapped both halves and left a ragged column. Anything the heading cannot
  // claim falls back to the note beside it, or to the rows.
  const headingTo = soleOwner && shared?.recipient ? shared.recipient : null;
  const headNote =
    shared === null || headingTo !== null
      ? null
      : shared.recipient !== null
        ? messages.inspector.openItemCounterparty(shared.recipient)
        : messages.inspector.openItemCounterpartyUnknown;
  return (
    <>
      <section className="mg-section">
        <div className="mg-section__headrow">
          <h3 className="mg-section__head">
            {headingTo !== null && soleOwner
              ? messages.inspector.openItemsByTo(soleOwner, headingTo)
              : soleOwner
                ? messages.inspector.openItemsBy(soleOwner)
                : messages.inspector.openItemsSection}
          </h3>
          {headNote !== null && (
            <span className="mg-section__note">{headNote}</span>
          )}
        </div>
        {model.openItems.length === 0 ? (
          <p className="mg-section__empty">
            {messages.inspector.emptyOpenItems(entityName)}
          </p>
        ) : (
          <ul className="mg-rows">
            {model.openItems.map((item, index) => (
              <OpenItemRow
                counterpartyStated={shared !== null}
                item={item}
                key={`${item.text}-${index}`}
                locale={locale}
                messages={messages}
                ownerStated={soleOwner !== null}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="mg-section">
        <div className="mg-section__headrow">
          <h3 className="mg-section__head">
            {messages.inspector.evidenceSection}
          </h3>
          {hiddenEvidence > 0 && (
            <span className="mg-section__note">
              {(model.evidenceTruncated
                ? messages.inspector.evidenceShownTruncated
                : messages.inspector.evidenceShown)(
                model.evidenceRows.length,
                model.viewAllCount,
              )}
            </span>
          )}
        </div>
        {model.evidencePending ? (
          // "Still reading" and "nothing supports this" are different answers,
          // and the second one may not stand in for the first while the page is
          // in flight.
          <p aria-busy className="mg-section__empty" role="status">
            {messages.inspector.evidenceReading}
          </p>
        ) : model.evidenceRows.length === 0 ? (
          <p className="mg-section__empty">
            {messages.inspector.emptyEvidence}
          </p>
        ) : (
          <>
            {quotes.length > 0 && (
              <ul className="mg-rows">
                {quotes.map((row, index) => (
                  <EvidenceRow
                    key={`${row.evidenceId}-${index}`}
                    locale={locale}
                    messages={messages}
                    onOpenEvidence={onOpenEvidence}
                    row={row}
                  />
                ))}
              </ul>
            )}
            {headers.length > 0 && (
              <HeaderLineGroup
                entityName={entityName}
                locale={locale}
                messages={messages}
                onOpenEvidence={onOpenEvidence}
                rows={headers}
                standalone={quotes.length === 0}
              />
            )}
          </>
        )}
      </section>
    </>
  );
}

/**
 * The folded header-line evidence: one line saying what these excerpts are, how
 * many there are and the span they cover, expanding to every quote verbatim.
 * Collapsed by default because the group's summary is the part that informs;
 * expandable because the citation must stay reachable, not merely counted.
 */
function HeaderLineGroup({
  rows,
  entityName,
  messages,
  locale,
  onOpenEvidence,
  standalone,
}: {
  rows: readonly InspectorEvidenceRow[];
  /** The panel's subject, so the sentence never lists it among "the others". */
  entityName: string;
  messages: MemoryGraphMessages;
  locale: string;
  onOpenEvidence?: (evidenceId: string) => void;
  /** True when no substantive excerpt precedes it, so it draws no divider. */
  standalone: boolean;
}) {
  // Always folded to begin with, including for a single line.
  //
  // It used to open a lone header line on sight, on the reasoning that folding
  // one row costs a click to reveal what fits on screen anyway. What actually
  // fit on screen was `- Cc: Zahide Öztürk <zahide@…>, Funda Özlü <funda@…>,
  // office@festavolo.eu <office@festavolo.eu>` — the whole of this entity's
  // answer to "where does this come from" being a dump of a mail header. The
  // line below says what that line says; the line itself is one click away.
  const [expanded, setExpanded] = useState(false);
  const range = instantRange(rows);
  const from = range ? formatDate(range.from, locale) : null;
  const to = range ? formatDate(range.to, locale) : null;
  // One mention is a thing that happened, and is read as such. Several are a
  // pattern, and the count plus the span they cover is the honest reading of
  // them — nobody learns anything from five near-identical sentences.
  const single =
    rows.length === 1 && rows[0]
      ? headerMentionText(rows[0], entityName, messages, locale)
      : null;
  return (
    <div className={`mg-hgroup${standalone ? " mg-hgroup--lead" : ""}`}>
      <button
        aria-expanded={expanded}
        className="mg-hgroup__toggle"
        onClick={() => setExpanded((value) => !value)}
        type="button"
      >
        <Icon icon={expanded ? ChevronDown : ChevronRight} size={14} />
        <Icon icon={Mail} size={13} />
        <span className="mg-hgroup__lead">
          {single ?? messages.inspector.evidenceHeaderGroup(rows.length)}
        </span>
        {/* The single-mention sentence already carries its own date. */}
        {single === null && from && to && (
          <span className="mg-hgroup__range">
            {messages.inspector.evidenceHeaderGroupRange(from, to)}
          </span>
        )}
        <span className="obsina-sr-only">
          {expanded
            ? messages.inspector.evidenceHeaderGroupHide
            : messages.inspector.evidenceHeaderGroupShow}
        </span>
      </button>
      {expanded && (
        <ul className="mg-rows mg-hgroup__rows">
          {rows.map((row, index) => (
            <EvidenceRow
              key={`${row.evidenceId}-${index}`}
              locale={locale}
              messages={messages}
              onOpenEvidence={onOpenEvidence}
              row={row}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One open commitment: the sentence, and the deadline in the slot beside it.
 *
 * The deadline shares the sentence's row rather than taking a line under it —
 * an alerting badge already lived there, and the half of the row the sentence
 * does not fill belongs to what the sentence is missing. A `dueSoon` badge is
 * itself the due date, so no plain date follows it; an `overdue` badge states
 * the lateness but not the date, so the date does follow. The slot wraps to its
 * own line rather than squeezing the sentence when both cannot fit.
 *
 * The promiser and the recipient are named here only when the section heading
 * could not state one for every row. Both are always stated somewhere: a
 * promise with no recorded recipient still says so, once.
 */
function OpenItemRow({
  item,
  ownerStated,
  counterpartyStated,
  messages,
  locale,
}: {
  item: InspectorOpenItem;
  /** True when the section heading already names this row's promiser. */
  ownerStated: boolean;
  /** True when the heading already states this row's recipient (or its absence). */
  counterpartyStated: boolean;
  messages: MemoryGraphMessages;
  locale: string;
}) {
  const badge = item.badge ? badgeText(item.badge, messages, locale) : null;
  const due = formatDate(item.dueDate, locale);
  const dueText =
    item.badge?.kind === "dueSoon"
      ? null
      : due
        ? messages.inspector.openItemDue(due)
        : messages.inspector.openItemDueUnknown;
  const ownerText =
    !ownerStated && item.ownerLabel
      ? messages.inspector.openItemOwner(item.ownerLabel)
      : null;
  // Stated both ways round when the heading could not: a row that silently
  // omits the recipient when the record names none is indistinguishable from
  // one where the reader missed it, and the record's silence is the answer.
  const counterpartyText = counterpartyStated
    ? null
    : item.counterpartyLabel
      ? messages.inspector.openItemCounterparty(item.counterpartyLabel)
      : messages.inspector.openItemCounterpartyUnknown;

  // An alerting badge owns the slot; the date it does not state (overdue) still
  // follows below it, exactly as before. Only an unalarming deadline moves up
  // into the slot, which is where the lines were being spent.
  const hasBadge = badge !== null && item.badge !== null;
  const metaDue = hasBadge ? dueText : null;

  return (
    <li className="mg-row">
      <div className="mg-row__lead">
        <p className="mg-row__text">
          {item.text}
          {!hasBadge && dueText !== null && (
            <span className="mg-row__when">{dueText}</span>
          )}
        </p>
        {hasBadge && item.badge && (
          <span className="mg-spill" style={toneVars(item.badge.tone)}>
            <span aria-hidden="true" className="mg-spill__dot" />
            {badge}
          </span>
        )}
      </div>
      {(metaDue !== null ||
        ownerText !== null ||
        counterpartyText !== null) && (
        <p className="mg-meta">
          {metaDue !== null && <span>{metaDue}</span>}
          {ownerText !== null && <span>{ownerText}</span>}
          {counterpartyText !== null && <span>{counterpartyText}</span>}
        </p>
      )}
    </li>
  );
}

/**
 * One evidence excerpt, presented for a person rather than a machine: the quote
 * leads because it is what the reader came for, and the provenance (date, source
 * channel, header-line and OCR markers) follows it in a single meta line. The
 * opaque provider/source identifier is never rendered — it is not carried on the
 * model at all.
 */
function EvidenceRow({
  row,
  messages,
  locale,
  onOpenEvidence,
}: {
  row: InspectorEvidenceRow;
  messages: MemoryGraphMessages;
  locale: string;
  onOpenEvidence?: (evidenceId: string) => void;
}) {
  const occurred = formatDate(row.occurredAt, locale);
  return (
    <li className="mg-row">
      <p className="mg-ev__quote">{row.quote}</p>
      <p className="mg-meta">
        <span>
          <Icon
            icon={EVIDENCE_ICON_COMPONENT[row.kind]}
            label={messages.evidenceKind[row.kind]}
            size={13}
          />
          {occurred ?? messages.inspector.evidenceDateUnknown}
        </span>
        {row.sourceChannel && (
          <span data-channel={row.sourceChannel}>
            <Icon
              icon={EVIDENCE_CHANNEL_ICON_COMPONENT[row.sourceChannel]}
              size={12}
            />
            {messages.evidenceChannel[row.sourceChannel]}
          </span>
        )}
        {row.isHeaderLine && (
          <span>{messages.inspector.evidenceHeaderLine}</span>
        )}
        {row.isDerived && (
          <span className="mg-meta__ocr" data-derived="true">
            <Icon icon={ScanText} size={12} />
            {messages.inspector.evidenceDerived}
            <span className="obsina-sr-only">
              {" "}
              {messages.inspector.evidenceDerivedHint}
            </span>
          </span>
        )}
      </p>
      {onOpenEvidence && (
        <button
          className="mg-ghost"
          onClick={() => onOpenEvidence(row.evidenceId)}
          type="button"
        >
          <Icon icon={ArrowUpRight} size={14} />
          {messages.inspector.openEvidence}
        </button>
      )}
    </li>
  );
}
